import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { Close, Warning } from '@mui/icons-material';
import { useUpdateBookingStatus } from '../../hooks/useBookings';
import { Booking } from '../../services/bookings';

interface CancelBookingModalProps {
  open: boolean;
  booking: Booking | null;
  onClose: () => void;
}

const cancellationReasons = [
  { value: 'client_request', label: 'Client requested cancellation' },
  { value: 'provider_unavailable', label: 'I am unavailable' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'duplicate', label: 'Duplicate booking' },
  { value: 'other', label: 'Other reason' },
];

export default function CancelBookingModal({ open, booking, onClose }: CancelBookingModalProps) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const updateStatus = useUpdateBookingStatus();

  const handleCancel = async () => {
    if (!reason) {
      setError('Please select a cancellation reason');
      return;
    }

    try {
      await updateStatus.mutateAsync({
        id: booking!._id,
        status: 'cancelled',
        cancellationReason: reason,
        cancellationNote: note,
      });

      setReason('');
      setNote('');
      setError(null);
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to cancel booking');
    }
  };

  const handleClose = () => {
    setReason('');
    setNote('');
    setError(null);
    onClose();
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#DC2626',
              }}
            >
              <Warning />
            </Box>
            <Typography variant="h6">Cancel Booking</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Are you sure you want to cancel the booking with <strong>{booking.clientName}</strong> for{' '}
          <strong>{booking.serviceName}</strong>?
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Cancellation Reason</InputLabel>
          <Select
            value={reason}
            label="Cancellation Reason"
            onChange={(e) => setReason(e.target.value)}
            required
          >
            {cancellationReasons.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Additional Notes (Optional)"
          multiline
          rows={3}
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Any additional details about the cancellation..."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={updateStatus.isPending}>
          Keep Booking
        </Button>
        <Button
          onClick={handleCancel}
          variant="contained"
          color="error"
          disabled={updateStatus.isPending}
        >
          {updateStatus.isPending ? 'Cancelling...' : 'Cancel Booking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
